import { MouseEvent, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconDatabase, IconDatabasePlus } from '@rossum/ui/icons/tabler';
import {
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { useTables } from '../../../../../datasets/hooks/useTables';

export const SelectDatasetChip = ({
  selectedDataset,
  setSelectedDataset,
  disabled,
  dataCy,
}: {
  selectedDataset: string;
  setSelectedDataset?: (dataset: string) => void;
  disabled?: boolean;
  dataCy?: string;
}) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { data: tables, isLoading } = useTables();

  const table = useMemo(() => {
    return tables?.find(t => t.name === selectedDataset) ?? null;
  }, [tables, selectedDataset]);

  return (
    <>
      {isLoading ? (
        <Chip
          disabled={disabled || isLoading}
          label={intl.formatMessage({
            id: 'features.queueSettings.fields.customLookup.selectDatasetChip.selectDataset',
          })}
          icon={<CircularProgress size={12} />}
          variant="filled"
        />
      ) : (
        <Tooltip placement="top" title={table?.metadata?.name ?? ''}>
          <Chip
            disabled={disabled || isLoading || !setSelectedDataset}
            data-cy={dataCy}
            label={
              table?.metadata?.name ??
              intl.formatMessage({
                id: 'features.queueSettings.fields.customLookup.selectDatasetChip.selectDataset',
              })
            }
            icon={
              <SvgIcon fontSize="medium">
                {selectedDataset ? <IconDatabase /> : <IconDatabasePlus />}
              </SvgIcon>
            }
            onClick={(event: MouseEvent<HTMLDivElement>) => {
              if (disabled) return;
              setAnchorEl(event.currentTarget);
            }}
            sx={{ maxWidth: '300px' }}
            variant="filled"
          />
        </Tooltip>
      )}
      {tables && tables.length > 0 ? (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Stack spacing={1} sx={{ width: '60vw' }}>
            <Stack pt={1} maxHeight="40vh" overflow="auto">
              {tables.map(t => (
                <MenuItem
                  key={t.name}
                  data-cy="dataset-option"
                  data-id={t.name}
                  onClick={() => {
                    setAnchorEl(null);
                    setSelectedDataset?.(t.name);
                  }}
                >
                  <ListItemIcon>
                    <SvgIcon fontSize="medium">
                      <IconDatabase />
                    </SvgIcon>
                  </ListItemIcon>
                  <ListItemText primary={t.metadata?.name} />
                </MenuItem>
              ))}
            </Stack>
          </Stack>
        </Popover>
      ) : null}
    </>
  );
};
